import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import LoadingSpinner from './LoadingSpinner';
import './statisticsModal.css';

ChartJS.register(ArcElement, Tooltip, Legend);

// 디바운싱 훅 - 객체를 위한 개선된 버전
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // 객체의 경우 JSON.stringify로 비교
    const currentValueStr = JSON.stringify(value);
    const debouncedValueStr = JSON.stringify(debouncedValue);
    
    // 값이 동일하면 타이머 설정하지 않음
    if (currentValueStr === debouncedValueStr) {
      return;
    }

    // 빈 값들인 경우 즉시 반영
    if (typeof value === 'object') {
      const hasValue = Object.values(value).some(v => v && v.toString().trim() !== '');
      if (!hasValue) {
        setDebouncedValue(value);
        return;
      }
    } else if (value === '' || value == null) {
      setDebouncedValue(value);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay, debouncedValue]);

  return debouncedValue;
};

// 진정한 가상 스크롤 테이블 컴포넌트 (div 기반)
const VirtualScrollTable = React.memo(({ records }) => {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);
  
  const ITEM_HEIGHT = 40; // 각 행의 고정 높이
  const CONTAINER_HEIGHT = 320; // 컨테이너 높이 (CSS와 일치)
  const VISIBLE_COUNT = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT);
  const BUFFER_SIZE = 5; // 버퍼 크기 (부드러운 스크롤을 위해)
  
  // 날짜 포맷팅을 미리 처리 (메모이제이션)
  const formattedRecords = useMemo(() => {
    return records.map(record => ({
      ...record,
      formattedDate: record.formattedDate || new Date(record.created_at).toLocaleDateString('ko-KR'),
      formattedWeight: record.formattedWeight || `${parseFloat(record.weight || 0).toFixed(3)} kg`
    }));
  }, [records]);
  
  // 현재 보이는 범위 계산
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
  const endIndex = Math.min(formattedRecords.length, startIndex + VISIBLE_COUNT + BUFFER_SIZE * 2);
  const visibleRecords = useMemo(() => 
    formattedRecords.slice(startIndex, endIndex),
    [formattedRecords, startIndex, endIndex]
  );
  
  // 스크롤 이벤트 핸들러 - 쓰로틀링 적용
  const handleScroll = useCallback((e) => {
    // requestAnimationFrame으로 스크롤 이벤트 최적화
    window.requestAnimationFrame(() => {
      setScrollTop(e.target.scrollTop);
    });
  }, []);
  
  return (
    <div className="virtual-scroll-container">
      {/* 헤더 */}
      <div className="virtual-table-header">
        <div className="virtual-cell" style={{ flex: '1' }}>날짜</div>
        <div className="virtual-cell" style={{ flex: '1' }}>어종</div>
        <div className="virtual-cell" style={{ flex: '0.8' }}>무게</div>
      </div>
      
      {/* 가상 스크롤 영역 */}
      <div 
        ref={containerRef}
        className="virtual-scroll-body"
        style={{ 
          height: CONTAINER_HEIGHT,
          overflow: 'auto',
          willChange: 'scroll-position', // GPU 가속
          transform: 'translateZ(0)' // GPU 레이어 강제 생성
        }}
        onScroll={handleScroll}
      >
        {/* 전체 높이를 나타내는 스페이서 */}
        <div style={{ 
          height: formattedRecords.length * ITEM_HEIGHT, 
          position: 'relative',
          willChange: 'transform' // GPU 가속
        }}>
          {/* 현재 보이는 행들만 렌더링 */}
          {visibleRecords.map((record, index) => {
            const actualIndex = startIndex + index;
            const isEvenRow = actualIndex % 2 === 0;
            
            return (
              <div
                key={`row-${actualIndex}`}
                className="virtual-table-row"
                style={{
                  position: 'absolute',
                  top: actualIndex * ITEM_HEIGHT,
                  left: 0,
                  right: 0,
                  height: ITEM_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid var(--border-color)',
                  backgroundColor: isEvenRow ? 'transparent' : 'rgba(255, 255, 255, 0.01)',
                  willChange: 'transform', // GPU 가속
                  transform: 'translateZ(0)' // GPU 레이어
                }}
              >
                <div className="virtual-cell" style={{ flex: '1', padding: '8px 10px', fontSize: '0.85rem' }}>
                  {record.formattedDate}
                </div>
                <div className="virtual-cell" style={{ flex: '1', padding: '8px 10px', fontSize: '0.85rem' }}>
                  {record.fish}
                </div>
                <div className="virtual-cell" style={{ flex: '0.8', padding: '8px 10px', fontSize: '0.85rem' }}>
                  {record.formattedWeight}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* 스크롤 정보 표시 */}
      <div className="virtual-scroll-info">
        <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
          총 {formattedRecords.length}개 기록
        </small>
      </div>
    </div>
  );
});

const StatisticsModal = ({ isOpen, onClose, type, itemName, initialFilters }) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [expandedBait, setExpandedBait] = useState(null); // 확장된 미끼/어종
  const [baitRecords, setBaitRecords] = useState({}); // 미끼/어종별 상세 기록
  const [baitLoadingStates, setBaitLoadingStates] = useState({}); // 미끼/어종별 로딩 상태
  
  // 어종별 미끼 상세 정보 (어종 확장 시 사용)
  const [fishBaitDetails, setFishBaitDetails] = useState({});
  const [fishBaitLoadingStates, setFishBaitLoadingStates] = useState({});
  
  // 지역 통계 탭 상태 (bait: 미끼별, fish: 어종별)
  const [locationTab, setLocationTab] = useState('bait');
  
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    region: '',
    category: '', // 기본값을 전체로 설정
    location: '',
    trophyFilter: '' // 트로피 필터
  });
  
  // 디바운싱이 필요한 입력 필터들
  const [inputFilters, setInputFilters] = useState({
    fish: '' // 어종 필터를 디바운싱 대상으로 분리
  });
  
  // 어종 필터 디바운싱 적용
  const debouncedInputFilters = useDebounce(inputFilters, 500);
  const [filterOptions, setFilterOptions] = useState({
    regions: [],
    categories: [],
    locations: []
  });
  
  // 날짜 빠른 설정 상태
  const [activeDateRange, setActiveDateRange] = useState('');
  
  // 필터 영역 접기/펼치기 상태 (기본값: 접힌 상태)
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // 필터 옵션 로드
  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await fetch('/api/records/filter-options?type=weekly');
      if (res.ok) {
        const options = await res.json();
        setFilterOptions(options);
      }
    } catch (error) {
      console.error('필터 옵션 로딩 에러:', error);
    }
  }, []);

  // 초기 필터 설정이 완료되었는지 추적
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchFilterOptions();
      // records 페이지의 현재 필터를 모달의 기본값으로 설정
      if (initialFilters) {
        setFilters(prev => ({
          ...prev,
          startDate: initialFilters.startDate || '',
          endDate: initialFilters.endDate || '',
          region: initialFilters.region || '',
          category: initialFilters.category || '',
          location: initialFilters.location || '',
          trophyFilter: initialFilters.trophyFilter || ''
        }));
        
        // 디바운싱 필터들 설정
        setInputFilters(prev => ({
          ...prev,
          fish: initialFilters.fish || ''
        }));
      }
      // 필터 초기화 완료 표시
      setFiltersInitialized(true);
    } else {
      // 모달이 닫힐 때 초기화 상태 리셋
      setFiltersInitialized(false);
    }
  }, [isOpen, fetchFilterOptions, initialFilters]);

  const generateChartData = useCallback((data) => {
    if (!data || data.length === 0) return null;

    // 상위 10개만 표시, 나머지는 "기타"로 합침
    const topItems = data.slice(0, 10);
    const otherItems = data.slice(10);
    
    let labels, values;
    
    if (type === 'location') {
      // location 타입일 때는 어종명과 평균 무게 정보를 포함
      labels = topItems.map(item => {
        const avgWeight = parseFloat(item.avg_weight || 0).toFixed(2);
        return `${item.name} (평균 ${avgWeight}kg)`;
      });
      values = topItems.map(item => parseInt(item.count) || 0);
    } else {
      // 기존 bait, fish 타입 처리
      labels = topItems.map(item => item.name);
      values = topItems.map(item => parseInt(item.count) || 0);
    }
    
    if (otherItems.length > 0) {
      const otherCount = otherItems.reduce((sum, item) => sum + (parseInt(item.count) || 0), 0);
      labels.push('기타');
      values.push(otherCount);
    }

    // 색상 팔레트
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#36A2EB',
      '#CCCCCC'
    ];

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: colors.slice(0, labels.length).map(color => color + '80'),
          borderWidth: 2,
        },
      ],
    };
  }, [type]);

  // 차트 데이터 로드
  const fetchStatistics = useCallback(async () => {
    if (!itemName) return;
    
    setLoading(true);
    try {
      let apiUrl = '';
      let queryParams = new URLSearchParams();
      
      if (type === 'location') {
        // 지역 통계 - 탭에 따라 미끼별 또는 어종별 통계
        if (locationTab === 'fish') {
          // 어종별 통계
          apiUrl = '/api/fish/statistics';
          queryParams = new URLSearchParams({
            location: itemName,
            ...Object.fromEntries(
              Object.entries(filters).filter(([_, value]) => value !== '')
            ),
            ...(debouncedInputFilters.fish && { fish: debouncedInputFilters.fish })
          });
        } else {
          // 미끼별 통계 (기본)
          apiUrl = '/api/bait/statistics';
          queryParams = new URLSearchParams({
            location: itemName,
            ...Object.fromEntries(
              Object.entries(filters).filter(([_, value]) => value !== '')
            ),
            ...(debouncedInputFilters.fish && { fish: debouncedInputFilters.fish })
          });
        }
      } else {
        // 기존 통계 (bait, fish)
        apiUrl = '/api/records/statistics';
        queryParams = new URLSearchParams({
          type: type,
          item: itemName,
          ...Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== '')
          ),
          ...(debouncedInputFilters.fish && { fish: debouncedInputFilters.fish })
        });
      }

      const res = await fetch(`${apiUrl}?${queryParams}`);
      if (res.ok) {
        const data = await res.json();
        if (type === 'location') {
          setChartData(data); // location 타입일 때는 원본 데이터 사용 (미끼별 통계)
        } else {
          setChartData(generateChartData(data)); // 다른 타입일 때는 차트 데이터로 변환
        }
      } else {
        console.error('통계 데이터 로딩 실패');
      }
    } catch (error) {
      console.error('통계 데이터 로딩 에러:', error);
    } finally {
      setLoading(false);
    }
  }, [itemName, type, filters, debouncedInputFilters, generateChartData, locationTab]);

  // 미끼/어종별 상세 기록 가져오기
  const fetchBaitRecords = useCallback(async (itemNameParam) => {
    // 이미 로드된 경우나 현재 로딩 중인 경우 스킵
    if (baitRecords[itemNameParam] || baitLoadingStates[itemNameParam]) return; 
    
    // 로딩 상태 설정
    setBaitLoadingStates(prev => ({
      ...prev,
      [itemNameParam]: true
    }));
    
    try {
      const queryParams = new URLSearchParams({
        type: 'weekly',
        limit: '100', // 가상 스크롤로 많은 데이터도 부드럽게 처리 가능
        location: itemName, // 현재 지역으로 제한
        detailsOnly: 'true', // 상세 정보만 요청 (fish, weight, created_at만)
        sortBy: 'time', // 최신순 정렬로 관련성 높은 데이터 우선
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        ),
        // 디바운싱된 입력 필터들 추가
        ...(debouncedInputFilters.fish && { fish: debouncedInputFilters.fish })
      });
      
      // 탭에 따라 미끼 또는 어종 필터 추가
      if (locationTab === 'fish') {
        queryParams.append('fish', itemNameParam);
      } else {
        queryParams.append('bait', itemNameParam);
      }

      const res = await fetch(`/api/records?${queryParams}`);
      if (res.ok) {
        const data = await res.json();
        // 데이터를 한 번에 설정하여 렌더링 최적화
        setBaitRecords(prev => ({
          ...prev,
          [itemNameParam]: data
        }));
      }
    } catch (error) {
      console.error('상세 기록 로딩 에러:', error);
    } finally {
      // 로딩 완료 상태 설정
      setBaitLoadingStates(prev => ({
        ...prev,
        [itemNameParam]: false
      }));
    }
  }, [filters, debouncedInputFilters, baitRecords, baitLoadingStates, itemName, locationTab]);

  // 어종별 미끼 상세 정보 가져오기 (어종 탭 전용)
  const fetchFishBaitDetails = useCallback(async (fishName) => {
    // 이미 로드된 경우나 현재 로딩 중인 경우 스킵
    if (fishBaitDetails[fishName] || fishBaitLoadingStates[fishName]) return;
    
    // 로딩 상태 설정
    setFishBaitLoadingStates(prev => ({
      ...prev,
      [fishName]: true
    }));
    
    try {
      const queryParams = new URLSearchParams({
        fish: fishName,
        location: itemName, // 현재 지역으로 제한
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const res = await fetch(`/api/fish/bait-details?${queryParams}`);
      if (res.ok) {
        const data = await res.json();
        // 데이터를 한 번에 설정하여 렌더링 최적화
        setFishBaitDetails(prev => ({
          ...prev,
          [fishName]: data
        }));
      }
    } catch (error) {
      console.error('어종별 미끼 상세 정보 로딩 에러:', error);
    } finally {
      // 로딩 완료 상태 설정
      setFishBaitLoadingStates(prev => ({
        ...prev,
        [fishName]: false
      }));
    }
  }, [filters, fishBaitDetails, fishBaitLoadingStates, itemName]);

  // 디바운스된 미끼/어종 확장/축소 토글
  const toggleBaitExpansion = useCallback((itemNameParam) => {
    // 같은 아이템 연속 클릭 방지
    if (expandedBait === itemNameParam) {
      setExpandedBait(null);
      return;
    }
    
    // 즉시 UI 업데이트 (낙관적 업데이트)
    setExpandedBait(itemNameParam);
    
    // 어종 탭일 때는 미끼 상세 정보 가져오기
    if (type === 'location' && locationTab === 'fish') {
      if (!fishBaitDetails[itemNameParam] && !fishBaitLoadingStates[itemNameParam]) {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            fetchFishBaitDetails(itemNameParam);
          });
        } else {
          setTimeout(() => {
            fetchFishBaitDetails(itemNameParam);
          }, 0);
        }
      }
    } else {
      // 미끼 탭이거나 다른 타입일 때는 기존 기록 가져오기
      if (!baitRecords[itemNameParam] && !baitLoadingStates[itemNameParam]) {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            fetchBaitRecords(itemNameParam);
          });
        } else {
          setTimeout(() => {
            fetchBaitRecords(itemNameParam);
          }, 0);
        }
      }
    }
  }, [expandedBait, baitRecords, baitLoadingStates, fetchBaitRecords, type, locationTab, fishBaitDetails, fishBaitLoadingStates, fetchFishBaitDetails]);

  useEffect(() => {
    if (isOpen && itemName && filtersInitialized) {
      fetchStatistics();
    }
  }, [isOpen, itemName, type, fetchStatistics, filtersInitialized]);

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setExpandedBait(null);
      setBaitRecords({});
      setBaitLoadingStates({});
      setFishBaitDetails({});
      setFishBaitLoadingStates({});
      setChartData(null);
    }
  }, [isOpen]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    
    // 날짜 필터가 수동으로 변경되면 활성 상태 초기화
    if (key === 'startDate' || key === 'endDate') {
      setActiveDateRange('');
    }
  }, []);

  // 날짜 빠른 설정 함수
  const setDateRange = useCallback((range) => {
    const today = new Date();
    // 로컬 시간대로 날짜 포맷팅 (UTC 변환 방지)
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    let startDate = '';
    let endDate = formatDate(today);
    
    switch (range) {
      case 'today':
        startDate = formatDate(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(yesterday);
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = formatDate(weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        startDate = formatDate(monthAgo);
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate,
      endDate
    }));
    setActiveDateRange(range);
  }, []);

  const resetFilters = useCallback(() => {
    // 일주일 전부터 오늘까지로 초기화 (records 페이지와 동일)
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setFilters({
      startDate: formatDate(weekAgo),
      endDate: formatDate(today),
      region: '',
      category: '', // 기본값을 전체로 설정
      location: '',
      trophyFilter: '' // 트로피 필터
    });
    setInputFilters({
      fish: '' // 어종 필터 초기화
    });
    setActiveDateRange('week'); // 날짜 범위 활성 상태를 'week'로 설정
  }, []);

  // 번역 함수들을 useMemo로 메모이제이션
  const translateRegion = useMemo(() => (region) => {
    const regionTranslations = {
      'gl': '모든 지역',
      'ru': '러시아',
      'de': '독일',
      'us': '미국',
      'fr': '프랑스',
      'cn': '중국',
      'pl': '폴란드',
      'kr': '한국',
      'jp': '일본',
      'en': '기타 국가',
      'id': '인도네시아'
    };
    return regionTranslations[region] || region;
  }, []);

  const translateCategory = useMemo(() => (category) => {
    const categoryTranslations = {
      'records': '종합기록',
      'ultralight': 'UL',
      'recordslight': '라이트',
      'bottomlight': '바텀 라이트',
      'sea': '해양',
      'telestick': '텔레스코픽'
    };
    return categoryTranslations[category] || category;
  }, []);

  // 필터 토글 함수
  const toggleFilters = useCallback(() => {
    setIsFiltersExpanded(prev => !prev);
  }, []);

  // 차트 옵션을 useMemo로 메모이제이션
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((sum, value) => sum + value, 0);
            const percentage = ((context.parsed * 100) / total).toFixed(1);
            return `${context.label}: ${context.parsed}개 (${percentage}%)`;
          }
        }
      }
    }
  }), []);

  // 모달 제목을 useMemo로 메모이제이션
  const modalTitle = useMemo(() => {
    if (type === 'bait') {
      return `"${itemName}" 미끼로 잡힌 어종 비율`;
    } else if (type === 'fish') {
      return `"${itemName}" 어종이 잡힌 미끼 비율`;
    } else if (type === 'location') {
      return `"${itemName}" 수역 통계`;
    }
    return '통계';
  }, [type, itemName]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{modalTitle}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* 지역 통계일 때만 탭 표시 */}
          {type === 'location' && (
            <div className="statistics-tabs">
              <button
                className={`tab-btn ${locationTab === 'bait' ? 'active' : ''}`}
                onClick={() => setLocationTab('bait')}
              >
                미끼별 통계
              </button>
              <button
                className={`tab-btn ${locationTab === 'fish' ? 'active' : ''}`}
                onClick={() => setLocationTab('fish')}
              >
                어종별 통계
              </button>
            </div>
          )}

          {/* 필터 영역 */}
          <div className="modal-filters">
            {/* 필터 토글 버튼 */}
            <div className="filter-toggle-header">
              <button 
                className="filter-toggle-btn"
                onClick={toggleFilters}
                type="button"
              >
                <span>{isFiltersExpanded ? '▼' : '▶'} 필터 설정</span>
              </button>
            </div>
            
            {/* 필터 내용 - 조건부 표시 */}
            {isFiltersExpanded && (
              <div className="filter-content">
                {/* 날짜 필터 영역 - 별도 구분 */}
                <div className="filter-row">
              <div className="filter-item">
                <label>시작 날짜:</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>
              <div className="filter-item">
                <label>종료 날짜:</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>
            </div>
            
            {/* 날짜 빠른 설정 버튼들 */}
            <div className="date-quick-buttons">
              <button
                type="button"
                className={`date-quick-btn ${activeDateRange === 'today' ? 'active' : ''}`}
                onClick={() => setDateRange('today')}
              >
                📅 오늘
              </button>
              <button
                type="button"
                className={`date-quick-btn ${activeDateRange === 'yesterday' ? 'active' : ''}`}
                onClick={() => setDateRange('yesterday')}
              >
                📅 어제
              </button>
              <button
                type="button"
                className={`date-quick-btn ${activeDateRange === 'week' ? 'active' : ''}`}
                onClick={() => setDateRange('week')}
              >
                📅 일주일
              </button>
              <button
                type="button"
                className={`date-quick-btn ${activeDateRange === 'month' ? 'active' : ''}`}
                onClick={() => setDateRange('month')}
              >
                📅 30일
              </button>
            </div>
            
            {/* 기타 필터들 - 첫 번째 줄 */}
            <div className="filter-row">
              <div className="filter-item">
                <label>지역:</label>
                <select
                  value={filters.region}
                  onChange={(e) => handleFilterChange('region', e.target.value)}
                >
                  <option value="">모든지역</option>
                  {filterOptions.regions.map((region) => (
                    <option key={region} value={region}>
                      {translateRegion(region)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-item">
                <label>채비:</label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">전체</option>
                  {filterOptions.categories.map((category) => (
                    <option key={category} value={category}>
                      {translateCategory(category)}
                    </option>
                  ))}
                </select>
              </div>
              {/* 수역 통계가 아닐 때만 맵 필터 표시 */}
              {type !== 'location' && (
                <div className="filter-item">
                  <label>맵:</label>
                  <select
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                  >
                    <option value="">전체</option>
                    {filterOptions.locations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            {/* 추가 필터들 - 두 번째 줄 */}
            <div className="filter-row">
              <div className="filter-item">
                <label>어종:</label>
                <input
                  type="text"
                  placeholder="어종 입력..."
                  value={inputFilters.fish}
                  onChange={(e) => setInputFilters(prev => ({ ...prev, fish: e.target.value }))}
                />
              </div>
              <div className="filter-item">
                <label>트로피:</label>
                <select
                  value={filters.trophyFilter}
                  onChange={(e) => handleFilterChange('trophyFilter', e.target.value)}
                >
                  <option value="">전체</option>
                  <option value="trophy">🏆 트로피</option>
                  <option value="rare">💎 레어 트로피</option>
                  <option value="none">일반</option>
                </select>
              </div>
            </div>
            
                {/* 초기화 버튼 - 오른쪽 아래 */}
                <div className="filter-reset-container">
                  <button className="reset-btn" onClick={resetFilters}>
                    🔄 초기화
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 차트/테이블 영역 */}
          <div className="chart-container">
            {loading ? (
              <div className="loading-container">
                <LoadingSpinner height={60} width={60} />
                <p style={{ marginTop: '16px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                  통계 데이터 로딩 중...
                </p>
              </div>
            ) : type === 'location' ? (
              // location 타입일 때는 탭에 따라 다른 테이블 표시
              <div className="statistics-table-wrapper">
                {chartData && chartData.length > 0 ? (
                  <table className="statistics-table expandable">
                    <thead>
                      <tr>
                        <th>순위</th>
                        <th>{locationTab === 'fish' ? '어종' : '미끼'}</th>
                        {locationTab === 'fish' && <th>미끼 종류</th>}
                        <th>잡힌 횟수</th>
                        <th>평균 무게</th>
                        <th>최대 무게</th>
                        <th>상세</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.map((item, index) => (
                        <React.Fragment key={index}>
                          <tr className="bait-row" onClick={() => toggleBaitExpansion(item.name)} style={{cursor: 'pointer'}}>
                            <td>{index + 1}</td>
                            <td style={{whiteSpace: 'pre-line'}}>
                              {locationTab === 'fish' ? item.name : item.name.replace(/ \/ /g, '\n')}
                            </td>
                            {locationTab === 'fish' && <td>{item.bait_count}종</td>}
                            <td>{item.count}회</td>
                            <td>{item.formattedAvgWeight || `${parseFloat(item.avg_weight || 0).toFixed(2)} kg`}</td>
                            <td>{item.formattedMaxWeight || `${parseFloat(item.max_weight || 0).toFixed(2)} kg`}</td>
                            <td>
                              <button 
                                className="expand-btn"
                                onClick={(e) => {
                                  e.stopPropagation(); // 이벤트 버블링 방지
                                  toggleBaitExpansion(item.name);
                                }}
                              >
                                {expandedBait === item.name ? '▼' : '▶'}
                              </button>
                            </td>
                          </tr>
                          {expandedBait === item.name && (
                            <tr className="expanded-row">
                              <td colSpan={locationTab === 'fish' ? 7 : 6}>
                                <div className="bait-details">
                                  {locationTab === 'fish' ? (
                                    // 어종 탭: 미끼 상세 정보 표시
                                    fishBaitLoadingStates[item.name] ? (
                                      <div className="loading-details">
                                        <LoadingSpinner height={32} width={32} />
                                        <span style={{ marginLeft: '12px', fontSize: '0.95rem' }}>
                                          미끼 정보 조회 중...
                                        </span>
                                      </div>
                                    ) : fishBaitDetails[item.name] ? (
                                      fishBaitDetails[item.name].length > 0 ? (
                                        <div className="statistics-table-wrapper">
                                          <table className="statistics-table">
                                            <thead>
                                              <tr>
                                                <th>순위</th>
                                                <th>미끼</th>
                                                <th>기록 수</th>
                                                <th>평균 무게</th>
                                                <th>최대 무게</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {fishBaitDetails[item.name].map((baitItem, baitIdx) => (
                                                <tr key={baitIdx}>
                                                  <td>{baitIdx + 1}</td>
                                                  <td style={{whiteSpace: 'pre-line'}}>{baitItem.name.replace(/ \/ /g, '\n')}</td>
                                                  <td>{baitItem.count}회</td>
                                                  <td>{baitItem.formattedAvgWeight || `${parseFloat(baitItem.avg_weight || 0).toFixed(2)} kg`}</td>
                                                  <td>{baitItem.formattedMaxWeight || `${parseFloat(baitItem.max_weight || 0).toFixed(2)} kg`}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className="no-data">
                                          <p>해당 어종을 잡은 미끼 기록이 없습니다.</p>
                                        </div>
                                      )
                                    ) : (
                                      <div className="no-data">
                                        <p>데이터를 불러오지 못했습니다.</p>
                                      </div>
                                    )
                                  ) : (
                                    // 미끼 탭: 기존 기록 표시
                                    baitLoadingStates[item.name] ? (
                                      <div className="loading-details">
                                        <LoadingSpinner height={32} width={32} />
                                        <span style={{ marginLeft: '12px', fontSize: '0.95rem' }}>
                                          기록 조회 중... (데이터 양에 따라 5~10초 소요될 수 있습니다)
                                        </span>
                                      </div>
                                    ) : baitRecords[item.name] ? (
                                      baitRecords[item.name].length > 0 ? (
                                        <VirtualScrollTable records={baitRecords[item.name]} />
                                      ) : (
                                        <div className="no-data">
                                          <p>해당 미끼로 잡은 기록이 없습니다.</p>
                                        </div>
                                      )
                                    ) : (
                                      <div className="no-data">
                                        <p>데이터를 불러오지 못했습니다.</p>
                                      </div>
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">
                    <p>해당 조건에 맞는 데이터가 없습니다.</p>
                  </div>
                )}
              </div>
            ) : chartData ? (
              // bait, fish 타입일 때는 원 그래프로 표시
              <div className="chart-wrapper">
                <Pie data={chartData} options={chartOptions} />
              </div>
            ) : (
              <div className="no-data">
                <p>해당 조건에 맞는 데이터가 없습니다.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// React.memo로 불필요한 리렌더링 방지
const MemoizedStatisticsModal = React.memo(StatisticsModal, (prevProps, nextProps) => {
  // props 비교 함수 - 실제로 중요한 props만 비교
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.type === nextProps.type &&
    prevProps.itemName === nextProps.itemName &&
    JSON.stringify(prevProps.initialFilters) === JSON.stringify(nextProps.initialFilters)
  );
});

export default MemoizedStatisticsModal;
